import React, { useState, useEffect, useContext } from "react";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { useTranslate, useDataProvider, Record } from "react-admin";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { debounce, TextField } from "@material-ui/core";
import { from } from "manipula";
import { ValueContext } from "./valueContext";
import { useMemo } from "react";

interface Props<T> {
  label: string;
  searchFields: string[];
  onSelect: (value: IResourceValue | null) => void;
  optionFactory: (dataItem: T) => IResourceValue | undefined | null;
  className?: string;
  loading?: boolean;
}

interface IResourceValue {
  id: number;
  name: string;
}

export const MuiAutocompleteInput = <T extends Record = Record>({
  label,
  searchFields,
  onSelect,
  optionFactory,
  className,
  loading,
}: Props<T>) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const resource = useSearchFields({}, searchFields);
  const [dropdownOptions, setDropdownOptions] = useState<IResourceValue[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const valueContext = useContext(ValueContext);

  const debouncedSetSearchText = useMemo(
    () =>
      debounce((nextValue: string) => {
        setSearchText(nextValue);
      }, 1000),
    []
  );

  useEffect(() => {
    dataProvider
      .getList<T>(resource, {
        pagination: { page: 1, perPage: 10 },
        sort: { field: "id", order: "ASC" },
        filter: { q: searchText },
      })
      .then((result) => {
        const mappedData = from(result.data.map(optionFactory))
          .where((x) => !!x)
          .toArray() as IResourceValue[];
        setDropdownOptions(mappedData);
      });
  }, [dataProvider, optionFactory, resource, searchText]);

  return (
    <div className={className}>
      <Autocomplete
        loading={loading}
        options={dropdownOptions}
        getOptionLabel={(option) => option.name}
        getOptionSelected={(option, value) => option.id === value.id}
        onChange={(_, v) => {
          onSelect(v);
          if (valueContext.setValue) {
            valueContext.setValue(v);
          }
        }}
        value={
          !!valueContext?.value ? (valueContext.value as IResourceValue) : null
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={translate(label, { _: label })}
            margin="dense"
            onChange={(e) => {
              debouncedSetSearchText(e.target.value);
            }}
          />
        )}
      />
    </div>
  );
};
